import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, TrendingUp, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

const ReopenStatsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const { data: resp } = await api.get('/super-admin/reopen-stats?days=90');
      setData(resp);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar estatísticas de reaberturas');
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const formatWhen = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('pt-PT'); } catch { return iso; }
  };

  return (
    <Card data-testid="reopen-stats-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-600" />
              Reaberturas de Denúncias (últimos 90 dias)
            </CardTitle>
            <CardDescription>
              Indicador de qualidade das investigações — muitas reaberturas por tenant sugerem decisões iniciais mal fundamentadas.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing} data-testid="refresh-reopen-stats">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : !data || data.total_reopenings === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Sem reaberturas registadas nos últimos 90 dias.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg border p-3 bg-blue-50 border-blue-200">
                <div className="text-xs text-blue-700">Total reaberturas</div>
                <div className="text-2xl font-bold text-blue-800" data-testid="total-reopenings">
                  {data.total_reopenings}
                </div>
              </div>
              <div className="rounded-lg border p-3 bg-slate-50 border-slate-200">
                <div className="text-xs text-slate-500">Tenants afectados</div>
                <div className="text-2xl font-bold text-slate-800" data-testid="distinct-tenants">
                  {data.distinct_tenants}
                </div>
              </div>
            </div>

            <table className="w-full text-sm" data-testid="reopen-stats-table">
              <thead className="border-b bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Cliente</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Reaberturas</th>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Última</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((t) => {
                  const highRisk = t.reopenings >= 5;
                  return (
                    <tr key={t.tenant_id} className="border-b hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{t.tenant_name}</div>
                        {t.tenant_slug && <div className="text-xs text-slate-500">{t.tenant_slug}</div>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Badge className={highRisk ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                          {t.reopenings}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{formatWhen(t.last_reopened_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReopenStatsCard;
